import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PolicyModal from './PolicyModal';
import { business } from '../data/business';

const { FiMail, FiMapPin, FiFileText } = FiIcons;

const Footer = () => {
  const [openPolicy, setOpenPolicy] = useState(null);

  const quickLinks = [
    { label: 'About Us', href: '#about' },
    { label: 'Products', href: '#products' },
    { label: 'Services', href: '#services' },
    { label: 'Process', href: '#process' },
    { label: 'Contact', href: '#contact' }
  ];

  const services = [
    { label: 'T-Shirts & Apparel', href: '#products' },
    { label: 'Fabrics & Raw Materials', href: '#products' },
    { label: 'Custom & Private Label Work', href: '#products' },
    { label: 'Wholesale & Bulk Trading', href: '#products' }
  ];

  const policyLinks = [
    { key: 'privacy', label: 'Privacy Policy' },
    { key: 'terms', label: 'Terms of Service' },
    { key: 'cookies', label: 'Cookie Policy' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-1"
          >
            <div className="flex items-center space-x-2 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg"></div>
              <span className="text-xl font-bold">{business.tradeName}</span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Your trusted partner for clothing, apparel, and textile trading. From fabrics to
              finished garments, we deliver quality products for brands, retailers, and bulk buyers.
            </p>
            <div className="flex items-start space-x-3 text-sm text-gray-500">
              <SafeIcon icon={FiFileText} className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>GSTIN: {business.gstin}</span>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-6">What We Provide</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.label}>
                  <a
                    href={service.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200"
                  >
                    {service.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-6">Contact Us</h3>
            <div className="space-y-4">
              <a
                href={`mailto:${business.email}`}
                className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors duration-200"
              >
                <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-400 flex-shrink-0" />
                <span>{business.email}</span>
              </a>
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-400">
                  {business.address.line1}<br />
                  {business.address.line2}<br />
                  {business.address.city}, {business.address.state} - {business.address.pin}
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4"
        >
          <p className="text-gray-400 text-sm text-center md:text-left">
            © {new Date().getFullYear()} {business.tradeName}. All rights reserved.
            <span className="block md:inline md:ml-2 text-gray-500">GSTIN: {business.gstin}</span>
          </p>
          <div className="flex space-x-6">
            {policyLinks.map((policy) => (
              <button
                key={policy.key}
                onClick={() => setOpenPolicy(policy.key)}
                className="text-gray-400 hover:text-white text-sm transition-colors duration-200"
              >
                {policy.label}
              </button>
            ))}
          </div>
        </motion.div>
      </div>

      <PolicyModal policyKey={openPolicy} onClose={() => setOpenPolicy(null)} />
    </footer>
  );
};

export default Footer;
